import React, { useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as LayoutIcon } from "assets/images/iconsV2/layout-alt-01.svg";
import { ReactComponent as ExpandAllIcon } from "assets/images/iconsV2/rows-02.svg";
import { ReactComponent as TagIcon } from "assets/images/iconsV2/tag-01.svg";
import NavContext from "components/molecules/Navigation/components/common/NavContext";
import testIds from "constants/testIds";
import { classNames } from "helpers/commonHelpers";
import { useUnitSystemPreferences } from "@RequestFormV2/hooks/useUnitSystemPreferences";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import {
  calculatePackingListTotals,
  formatPackingListWeight,
} from "@RequestFormV2/sections/PackingList/utils/packingListCalculations";
import { useFreightModeHeaderSummary } from "@RequestFormV2/components/RequestFormHeader/hooks/useFreightModeHeaderSummary";
import { useIncotermsHeaderSummary } from "@RequestFormV2/components/RequestFormHeader/hooks/useIncotermsHeaderSummary";
import { useScheduleHeaderSummary } from "@RequestFormV2/components/RequestFormHeader/hooks/useScheduleHeaderSummary";
import { useProviderHeaderSummary } from "@RequestFormV2/components/RequestFormHeader/hooks/useProviderHeaderSummary";
import ProviderHeaderAvatar from "@RequestFormV2/components/RequestFormHeader/ProviderHeaderAvatar";
import { useRequestFormSave } from "@RequestFormV2/context/save";

interface RequestFormHeaderProps {
  onExpandAll: () => void;
  onOpenTemplate: () => void;
  isEditQuoteRequest?: boolean;
  showMobileMenu?: boolean;
  isEditShipment?: boolean;
  isEditQuickRequest?: boolean;
  headingMessageId: string;
  showProviderSummary?: boolean;
}

function formatHeaderStopValue(summary: { location: string; dateTime: string } | null): React.ReactNode {
  if (!summary?.location && !summary?.dateTime) {
    return "-";
  }
  if (summary.location && summary.dateTime) {
    return (
      <>
        {summary.location}
        <br />
        {summary.dateTime}
      </>
    );
  }
  return summary.location || summary.dateTime;
}

export default function RequestFormHeader({
  onExpandAll,
  onOpenTemplate,
  isEditQuoteRequest = false,
  showMobileMenu = false,
  isEditShipment = false,
  isEditQuickRequest = false,
  headingMessageId,
  showProviderSummary = true,
}: RequestFormHeaderProps) {
  const intl = useIntl();
  const { shipmentId } = useParams();
  const { weightUnit } = useUnitSystemPreferences();
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const { pickupSummary, deliverySummary } = useScheduleHeaderSummary();
  const { incotermsLabel } = useIncotermsHeaderSummary();
  const { label: freightModeLabel } = useFreightModeHeaderSummary({
    enabled: showProviderSummary,
  });
  const {
    count: providerCount,
    avatars,
    overflowCount,
  } = useProviderHeaderSummary({
    enabled: showProviderSummary,
  });
  const saveContext = useRequestFormSave();
  const isDraftSaving = saveContext?.isDraftSaving ?? false;
  const draftSaved = useWatch({ name: "draftSaved" });
  const packingList = (useWatch({ name: "packing_list" }) as PackingListRow[] | undefined) ?? [];

  const totals = useMemo(() => calculatePackingListTotals(packingList, weightUnit), [packingList, weightUnit]);
  const isMobile = navigationConfig?.isMobile ?? false;

  const handleOpenMobileMenu = () => {
    setNavigationConfig({
      ...navigationConfig,
      expanded: true,
      isShown: true,
    });
  };

  return (
    <header
      className={classNames("rfv2-header", {
        "rfv2-header--expanded": navigationConfig?.expanded,
        "rfv2-header--mobile-menu": showMobileMenu,
      })}
    >
      {showMobileMenu && (
        <div className="rfv2-header__mobile-nav">
          <button type="button" className="rfv2-header__menu-btn" onClick={handleOpenMobileMenu} aria-label="Open menu">
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      )}
      <div className="rfv2-header__title-row">
        <div className="rfv2-header__title-group">
          <div className="rfv2-header__heading">
            {intl.formatMessage({
              id: headingMessageId,
            })}
            {(isEditShipment || isEditQuoteRequest || isEditQuickRequest) && shipmentId ? ` ${shipmentId}` : null}
          </div>
          {(isDraftSaving || draftSaved) && (
            <span className="rfv2-header__draft-saved">
              {intl.formatMessage({
                id: isDraftSaving ? "request-form.header.draftSaving" : "request-form.header.draftSaved",
              })}
            </span>
          )}
        </div>
        <div className="rfv2-header__actions">
          <button
            className="rfv2-header__action-btn"
            type="button"
            data-testid={testIds.LOAD_TEMPLATE_BUTTON}
            onClick={onOpenTemplate}
          >
            <LayoutIcon />
            {intl.formatMessage({
              id: isMobile ? "request-form.header.loadTemplateShort" : "request-form.header.loadTemplate",
              defaultMessage: isMobile ? "Template" : "Load template",
            })}
          </button>
          <button className="rfv2-header__expand-btn" type="button" onClick={onExpandAll}>
            <ExpandAllIcon />
            {intl.formatMessage({
              id: isMobile ? "request-form.header.expandAllSectionsShort" : "request-form.header.expandAllSections",
              defaultMessage: isMobile ? "Expand" : "Expand all",
            })}
          </button>
        </div>
      </div>

      <div className="rfv2-header__details-row">
        {showProviderSummary && (
          <div className="rfv2-header__field rfv2-header__field--wide">
            <span className="rfv2-header__field-label">{intl.formatMessage({ id: "general.freightMode" })}</span>
            <div className="rfv2-header__badges">
              <span className="rfv2-header__badge">
                <TagIcon aria-hidden />
                {freightModeLabel}
              </span>
            </div>
          </div>
        )}

        {/* Incoterms */}
        <div className="rfv2-header__field rfv2-header__field--incoterms">
          <span className="rfv2-header__field-label">{intl.formatMessage({ id: "general.incoterms" })}</span>
          <span className="rfv2-header__field-value">{incotermsLabel || "-"}</span>
        </div>

        {/* Pickup */}
        <div className="rfv2-header__field rfv2-header__field--wide">
          <span className="rfv2-header__field-label">{intl.formatMessage({ id: "general.pickup" })}</span>
          <span className="rfv2-header__field-value">{formatHeaderStopValue(pickupSummary)}</span>
        </div>

        {/* Delivery */}
        <div className="rfv2-header__field rfv2-header__field--wide">
          <span className="rfv2-header__field-label">{intl.formatMessage({ id: "general.delivery" })}</span>
          <span className="rfv2-header__field-value">{formatHeaderStopValue(deliverySummary)}</span>
        </div>

        {/* Total weight */}
        <div className="rfv2-header__field rfv2-header__field--weight">
          <span className="rfv2-header__field-label">{intl.formatMessage({ id: "general.totalWeight" })}</span>
          <span className="rfv2-header__field-value">
            {formatPackingListWeight(totals.totalWeight)} {weightUnit}
            {totals.totalUnits > 0 && (
              <>
                <br />
                {intl.formatMessage(
                  { id: "request-form.header.packingListUnits", defaultMessage: "{count} units" },
                  { count: totals.totalUnits },
                )}
              </>
            )}
          </span>
        </div>

        {/* Provider */}
        {showProviderSummary && (
          <div className="rfv2-header__field rfv2-header__field--provider">
            <span className="rfv2-header__field-label">
              {intl.formatMessage({ id: "general.provider" })} ({providerCount})
            </span>
            {providerCount > 0 ? (
              <div className="rfv2-header__provider-info">
                <div className="rfv2-header__avatar-stack">
                  {avatars.map((provider) => (
                    <ProviderHeaderAvatar key={provider.id} provider={provider} />
                  ))}
                </div>
                {overflowCount > 0 && <span className="rfv2-header__avatar-count">+{overflowCount}</span>}
              </div>
            ) : (
              <span className="rfv2-header__field-value">-</span>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
